/**
 * ENG-5641 — read-only HTTP connectivity probes for the direct-API ('http_provider')
 * integrations (Linear, Google Workspace, Xero, v0, Buffer).
 *
 * Centralized in core so BOTH consumers share one implementation (DRY):
 *   - the manager-CLI host-side probe executor, and
 *   - the API `POST /integrations/:id/test` endpoint (when it adopts the resolver).
 *
 * `fetch` is injected so this is unit-testable without network. Every probe is
 * a single read-only call (a `viewer`/`userinfo`/`connections`/`user` GET or a
 * GraphQL `viewer` query) — no mutations, ever.
 *
 * Outcome mapping (to the ConnectivityStatus vocabulary):
 *   - 'ok'              — reachable and the read returned a usable result
 *   - 'down'            — auth rejected (401/403) or a semantic dead-end
 *                         (no viewer, no connected orgs) — needs attention
 *   - 'transient_error' — network/timeout or 5xx — retryable, don't escalate yet
 */

import type { ConnectivityProbeOutcome } from './connectivity-probe.js';

const PROBE_TIMEOUT_MS = 10_000;

interface HttpCreds {
  api_key?: string;
  access_token?: string;
  [k: string]: unknown;
}

async function timedFetch(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    return await fetchImpl(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Map a non-ok HTTP response to the right connectivity status. */
function statusForHttp(httpStatus: number): 'down' | 'transient_error' {
  if (httpStatus === 401 || httpStatus === 403) return 'down';
  if (httpStatus >= 500) return 'transient_error';
  return 'down';
}

function networkOutcome(err: unknown): ConnectivityProbeOutcome {
  const isAbort = (err as Error)?.name === 'AbortError';
  return {
    status: 'transient_error',
    message: isAbort ? `Connection timed out after ${PROBE_TIMEOUT_MS / 1000}s` : `Connection failed: ${(err as Error).message}`,
  };
}

async function probeLinear(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  // Linear accepts the API key in Authorization WITHOUT a Bearer prefix.
  const key = creds.api_key ?? creds.access_token;
  if (!key) return { status: 'down', message: 'No Linear credential present' };
  try {
    const res = await timedFetch(fetchImpl, 'https://api.linear.app/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: String(key) },
      body: JSON.stringify({ query: '{ viewer { id name email } }' }),
    });
    if (!res.ok) return { status: statusForHttp(res.status), message: `Linear API returned ${res.status}` };
    const body = (await res.json()) as {
      data?: { viewer?: { name?: string; email?: string } };
      errors?: Array<{ message: string }>;
    };
    if (body.errors?.length) return { status: 'down', message: body.errors[0]?.message ?? 'Unknown Linear error' };
    const viewer = body.data?.viewer;
    if (!viewer) return { status: 'down', message: 'Invalid key — no viewer returned' };
    return { status: 'ok', message: `Connected as ${viewer.name ?? viewer.email ?? 'unknown'}` };
  } catch (err) {
    return networkOutcome(err);
  }
}

async function probeBearerJson(
  url: string,
  creds: HttpCreds,
  fetchImpl: typeof fetch,
  interpret: (body: unknown) => ConnectivityProbeOutcome,
  // ENG-6100: some providers reject requests without extra headers — GitHub
  // 403s any request missing a `User-Agent`, which would otherwise be
  // misclassified as `down` (a false "reconnect required").
  extraHeaders?: Record<string, string>,
): Promise<ConnectivityProbeOutcome> {
  const token = creds.access_token ?? creds.api_key;
  if (!token) return { status: 'down', message: 'No credential present' };
  try {
    const res = await timedFetch(fetchImpl, url, { headers: { Authorization: `Bearer ${token}`, ...extraHeaders } });
    if (!res.ok) {
      const message = res.status === 401 ? 'Token expired or revoked — reconnect required' : `API returned ${res.status}`;
      return { status: statusForHttp(res.status), message };
    }
    return interpret(await res.json());
  } catch (err) {
    return networkOutcome(err);
  }
}

async function probeBuffer(creds: HttpCreds, fetchImpl: typeof fetch): Promise<ConnectivityProbeOutcome> {
  // ENG-6642: Buffer's GraphQL API authenticates with an API key sent as a
  // Bearer token. (Buffer rejects OIDC tokens for direct API calls — the API
  // key minted at publish.buffer.com/settings/api is the only accepted
  // credential.) The lightest authenticated read is the account's
  // organizations: it proves the key is valid AND that at least one Buffer
  // organization is reachable, which every other call needs.
  const key = creds.api_key ?? creds.access_token;
  if (!key) return { status: 'down', message: 'No Buffer credential present' };
  try {
    const res = await timedFetch(fetchImpl, 'https://api.buffer.com', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${key}` },
      body: JSON.stringify({ query: '{ account { organizations { id name } } }' }),
    });
    if (!res.ok) {
      const message =
        res.status === 401
          ? 'Buffer API key expired or revoked — reconnect required'
          : `Buffer API returned ${res.status}`;
      return { status: statusForHttp(res.status), message };
    }
    const body = (await res.json()) as {
      data?: { account?: { organizations?: Array<{ id?: string; name?: string }> } };
      errors?: Array<{ message: string }>;
    };
    if (body.errors?.length) return { status: 'down', message: body.errors[0]?.message ?? 'Unknown Buffer error' };
    const orgs = body.data?.account?.organizations ?? [];
    if (!orgs.length) return { status: 'down', message: 'No Buffer organizations on this account' };
    return { status: 'ok', message: `Connected to ${orgs[0]?.name ?? 'Buffer'}` };
  } catch (err) {
    return networkOutcome(err);
  }
}

const PROBE_DEFINITIONS = new Set(['linear', 'google-workspace', 'xero', 'v0', 'github', 'buffer']);

/** True when {@link probeHttpProvider} knows how to probe this definition. */
export function isHttpProbeProvider(definitionId: string): boolean {
  return PROBE_DEFINITIONS.has(definitionId);
}

/**
 * Run the read-only HTTP probe for a direct-API provider. Returns `null` for a
 * definition this module doesn't know — callers should treat that as "no probe".
 */
export async function probeHttpProvider(
  definitionId: string,
  credentials: HttpCreds,
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectivityProbeOutcome | null> {
  switch (definitionId) {
    case 'linear':
      return probeLinear(credentials, fetchImpl);
    case 'buffer':
      return probeBuffer(credentials, fetchImpl);
    case 'google-workspace':
      return probeBearerJson('https://www.googleapis.com/oauth2/v2/userinfo', credentials, fetchImpl, (body) => {
        const info = body as { name?: string; email?: string };
        return { status: 'ok', message: `Connected as ${info.name ?? info.email ?? 'unknown'}` };
      });
    case 'xero':
      return probeBearerJson('https://api.xero.com/connections', credentials, fetchImpl, (body) => {
        const conns = (body ?? []) as Array<{ tenantName?: string }>;
        if (!conns.length) return { status: 'down', message: 'No Xero organisations connected' };
        return { status: 'ok', message: `Connected to ${conns[0]?.tenantName ?? 'Xero'}` };
      });
    case 'v0':
      return probeBearerJson('https://api.v0.dev/v1/user', credentials, fetchImpl, (body) => {
        const user = body as { name?: string; email?: string };
        return { status: 'ok', message: `Connected as ${user.name ?? user.email ?? 'unknown'}` };
      });
    case 'github':
      // ENG-6100: works for both auth shapes — OAuth access_token and a PAT
      // (api_key) both authenticate `GET /user` as a Bearer token. The
      // User-Agent header is mandatory (GitHub 403s without it). Message
      // states what was OBSERVED ("Reached GitHub as <login>"), not that the
      // agent's tools will work — auth is not the same as token scope (the
      // missing-scopes signal is surfaced separately by the Test route).
      return probeBearerJson('https://api.github.com/user', credentials, fetchImpl, (body) => {
        const u = body as { login?: string; name?: string };
        return { status: 'ok', message: `Reached GitHub as ${u.login ?? u.name ?? 'unknown'}` };
      }, { 'User-Agent': 'augmented-team-connectivity-probe', 'X-GitHub-Api-Version': '2026-03-10' });
    default:
      return null;
  }
}
